import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { 
  Wrench, 
  Truck, 
  Settings, 
  Home as HomeIcon, 
  PenTool,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Star
} from "lucide-react";

const services = [
  {
    id: "livestock-infrastructure",
    icon: HomeIcon,
    title: "Livestock Infrastructure",
    subtitle: "Built for Australian Farming",
    description: "Heavy-duty shelters for livestock and horses, plus cattle yards, gates, and handling equipment. Our horse shelters are one of our most popular products – built tough to protect your animals from sun, wind, and rain.",
    features: [
      "Horse shelters (our best seller!)",
      "Livestock shelters and sheds",
      "Cattle yards and races",
      "Gates and panels",
      "Hay feeders",
      "Custom handling equipment"
    ],
    forWho: "Horse owners, cattle farmers, sheep farmers, hobby farmers, and agricultural businesses.",
    images: ["/IMG_6296.jpeg"],
    highlight: "Horse Shelters",
    productLink: "/products#livestock-shelters"
  },
  {
    id: "welding-fabrication",
    icon: Wrench,
    title: "Welding & Fabrication",
    subtitle: "Custom Metal Work for Any Application",
    description: "From structural steel to precision components, our workshop handles all types of metal fabrication. We work with steel, aluminium, and stainless steel to create exactly what you need.",
    features: [
      "MIG, TIG, and Stick welding",
      "Steel, aluminium, and stainless steel",
      "Structural fabrication",
      "Repairs and modifications",
      "Custom brackets and components"
    ],
    forWho: "Farmers, contractors, builders, and anyone needing quality metal fabrication.",
    images: ["/IMG_6300.jpeg"],
    contactLink: true
  },
  {
    id: "custom-attachments",
    icon: Settings,
    title: "Stick Rakes & Custom Attachments",
    subtitle: "Built for Your Machine",
    description: "We manufacture heavy-duty stick rakes, loader buckets, fork frames, and machinery attachments. Our stick rakes are a customer favourite – perfect for clearing land and debris.",
    features: [
      "Stick rakes (top seller!)",
      "Loader buckets (various sizes)",
      "Fork frames and pallet forks",
      "Grab buckets and grapples",
      "Custom hitch options"
    ],
    forWho: "Farmers, earthmoving contractors, landscapers, and anyone operating loaders or telehandlers.",
    images: ["/IMG_6280_stickrake_main.jpg", "/IMG_6309_stickrake.jpg", "/IMG_6311_bucket.jpg"],
    highlight: "Stick Rakes",
    productLink: "/products#stick-rakes-attachments"
  },
  {
    id: "cad-design",
    icon: PenTool,
    title: "CAD Design & Custom Projects",
    subtitle: "From Concept to Reality",
    description: "Have a unique problem that needs a custom solution? We offer CAD design services to plan and visualise your project before fabrication begins. See your design come to life!",
    features: [
      "CAD design and drafting",
      "3D visualisation",
      "Engineering consultation",
      "Prototype development",
      "Complete project management"
    ],
    forWho: "Businesses with unique requirements, inventors, and anyone needing a custom-engineered solution.",
    images: ["/IMG_6306.jpg", "/IMG_6298_cad_real.jpeg", "/IMG_6304_real.jpg", "/IMG_6291_sketch.jpg"],
    darkArrows: true,
    productLink: "/products#bespoke-projects"
  },
  {
    id: "mobile-welding",
    icon: Truck,
    title: "Mobile Onsite Welding",
    subtitle: "We Come to You",
    description: "Can't bring it to the workshop? No problem. Our fully-equipped mobile welding rig comes to your farm, construction site, or wherever you need us. Fast response for breakdowns and scheduled maintenance.",
    features: [
      "Fully equipped mobile workshop",
      "Farm and site visits",
      "Emergency breakdown repairs",
      "Equipment modifications on-site",
      "Serving the Great Southern region"
    ],
    forWho: "Farmers with broken machinery, construction sites, remote locations, and anyone who can't transport their equipment.",
    images: ["/IMG_6297.jpeg"],
    contactLink: true
  }
];

// Image Carousel for Services
function ServiceImageCarousel({ images, name, darkArrows = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const touchDataRef = useRef({ startX: 0, startY: 0, endX: 0, isSwiping: false });
  const currentIndexRef = useRef(currentIndex);

  // Keep ref in sync with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const minSwipeDistance = 30;

  const goToImage = (index) => {
    setCurrentIndex(index);
    currentIndexRef.current = index;
  };

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current < images.length - 1) {
      goToImage(currentIndexRef.current + 1);
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current > 0) {
      goToImage(currentIndexRef.current - 1);
    }
  };

  // Use native event listeners for touch to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      touchDataRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        endX: e.touches[0].clientX,
        isSwiping: false
      };
    };

    const handleTouchMove = (e) => {
      const { startX, startY } = touchDataRef.current;
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);

      // If horizontal movement is greater than vertical, prevent page scroll
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
        touchDataRef.current.isSwiping = true;
      }
      touchDataRef.current.endX = currentX;
    };

    const handleTouchEnd = () => {
      const { startX, endX } = touchDataRef.current;
      const distance = startX - endX;
      
      if (Math.abs(distance) > minSwipeDistance && images.length > 1) {
        if (distance > 0 && currentIndexRef.current < images.length - 1) {
          goToImage(currentIndexRef.current + 1);
        } else if (distance < 0 && currentIndexRef.current > 0) {
          goToImage(currentIndexRef.current - 1);
        }
      }
      touchDataRef.current.isSwiping = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [images.length]); // Only re-attach if images array length changes

  if (!images || images.length === 0) return null;
  
  if (images.length === 1) {
    return (
      <img 
        src={images[0]}
        alt={name}
        className="w-full h-full object-cover"
      />
    );
  }

  // Arrow styles based on darkArrows prop
  const arrowBg = darkArrows ? 'bg-black/40 hover:bg-black/60 border-black/30' : 'bg-white/10 hover:bg-white/20 border-white/20';
  const arrowColor = darkArrows ? 'text-black' : 'text-white';
  const arrowVisibility = darkArrows ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';

  return (
    <div 
      ref={containerRef}
      className="w-full h-full relative group"
      style={{ touchAction: 'pan-y' }}
    >
      <div 
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((img, idx) => (
          <img 
            key={idx}
            src={img}
            alt={`${name} - ${idx + 1}`}
            className="w-full h-full object-cover flex-shrink-0 select-none"
            draggable="false"
          />
        ))}
      </div>
      
      {/* Swipe hint mobile */}
      {images.length > 1 && (
        <div className="absolute top-2 right-2 md:hidden">
          <div className="px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full shadow-lg border border-white/10">
            <span className="text-white text-xs font-bold uppercase tracking-wider">Swipe</span>
          </div>
        </div>
      )}
      
      {/* Desktop arrows */}
      <button
        onClick={prevImage}
        className={`absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 ${arrowBg} backdrop-blur-sm rounded-full hidden md:flex items-center justify-center ${arrowVisibility} transition-all border`}
      >
        <ChevronLeft className={`w-4 h-4 ${arrowColor}`} />
      </button>
      <button
        onClick={nextImage}
        className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 ${arrowBg} backdrop-blur-sm rounded-full hidden md:flex items-center justify-center ${arrowVisibility} transition-all border`}
      >
        <ChevronRight className={`w-4 h-4 ${arrowColor}`} />
      </button>
      
      {/* Counter and dots */}
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
        <span className={`px-2 py-0.5 ${darkArrows ? 'bg-black/50' : 'bg-black/50'} backdrop-blur-sm rounded-full text-white text-xs`}>
          {currentIndex + 1}/{images.length}
        </span>
        <div className="flex gap-1">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); goToImage(idx); }}
              className={`h-1.5 rounded-full transition-all ${
                idx === currentIndex ? 'bg-[#E63946] w-4' : 'bg-white/40 w-1.5'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const location = useLocation();
  const containerRef = useRef(null);

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = containerRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-transition pt-20" data-testid="services-page" ref={containerRef}>
      <Helmet>
        <title>Welding Services Albany | Horse Shelter Construction & Stick Rake Manufacturing | PWC Great Southern</title>
        <meta name="description" content="PWC Fabrication services Albany: Horse shelter construction, stick rake manufacturing, mobile welding, custom loader buckets & livestock infrastructure. Serving Albany & Great Southern Western Australia." />
        <meta name="keywords" content="welding services Albany, welding Great Southern, fabrication Albany, horse shelter construction WA, stick rake manufacturing, mobile welding Albany, custom fabrication Great Southern, loader buckets WA" />
        <link rel="canonical" href="https://pwcengineering.com.au/services" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm animate-on-scroll">
              Our Services
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase text-white mb-6 animate-on-scroll delay-100">
              What We Do
            </h1>
            <p className="text-xl text-[#A3A3A3] leading-relaxed animate-on-scroll delay-200">
              From workshop fabrication to mobile repairs, we offer a complete range of 
              welding and engineering services for the agricultural, construction, and 
              4x4 industries.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-[#0A0A0A]">
        {services.map((service, index) => (
          <div key={service.id}>
            {/* Mobile Divider - shows only on mobile, not before first item */}
            {index > 0 && (
              <div className="md:hidden px-6">
                <div className="h-0.5 bg-gradient-to-r from-transparent via-[#E63946] to-transparent" />
              </div>
            )}
            <div 
              id={service.id}
              className={`py-20 md:py-32 ${index % 2 === 1 ? 'bg-[#121212]' : 'bg-[#0A0A0A]'}`}
              data-testid={`service-section-${service.id}`}
            >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 border-2 border-[#E63946]/40 rounded-2xl flex items-center justify-center bg-[#0A0A0A]">
                      <span className="text-[#E63946] text-2xl font-black tracking-tight">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="px-3 py-1 bg-[#E63946]/10 rounded-full text-[#E63946] font-semibold uppercase tracking-widest text-xs">
                      {service.subtitle}
                    </span>
                  </div>
                  
                  <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
                    {service.title}
                  </h2>
                  
                  <p className="text-lg text-[#A3A3A3] leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="mb-8">
                    <h4 className="text-white font-bold uppercase tracking-wider mb-4">
                      What's Included:
                    </h4>
                    <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          {feature.includes('top seller') || feature.includes('best seller') ? (
                            <Star className="text-[#E63946] flex-shrink-0 mt-0.5" size={16} fill="#E63946" />
                          ) : (
                            <CheckCircle2 className="text-[#E63946] flex-shrink-0 mt-0.5" size={16} />
                          )}
                          <span className={feature.includes('top seller') || feature.includes('best seller') ? 'text-white font-semibold' : 'text-[#A3A3A3]'}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl mb-8">
                    <p className="text-sm text-[#A3A3A3]">
                      <span className="text-white font-semibold">Who it's for: </span>
                      {service.forWho}
                    </p>
                  </div>

                  {/* Desktop CTA - Get a Quote (only for services WITHOUT contactLink) */}
                  {!service.contactLink && (
                  <Link to="/contact" className="hidden md:inline-block">
                    <Button 
                      className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full btn-glow"
                      data-testid={`${service.id}-quote-btn`}
                    >
                      Get a Quote
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                  )}
                
                {/* CAD Design Photo - Mobile only, shown between description and button */}
                {service.id === 'cad-design' && (
                  <div className="mt-8 md:hidden">
                    <div className="aspect-[4/3] bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
                      <ServiceImageCarousel images={service.images} name={service.title} darkArrows={service.darkArrows} />
                    </div>
                  </div>
                )}
                
                {/* Mobile CTA Button - See Products - only show if service has productLink */}
                {service.productLink && (
                <div className="mt-8 md:hidden">
                  <Link to={service.productLink}>
                    <Button 
                      className="w-full bg-transparent border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white font-bold uppercase tracking-wider px-8 py-4 text-lg rounded-full transition-all group"
                      data-testid={`mobile-see-products-${service.id}`}
                    >
                      See Products
                      <ArrowRight className="ml-2 animate-bounce-x" size={20} />
                    </Button>
                  </Link>
                </div>
                )}

                {/* Mobile & Desktop CTA Button - Contact Us - only show if service has contactLink */}
                {service.contactLink && (
                <div className="mt-8">
                  <Link to="/contact">
                    <Button 
                      className="w-full md:w-auto bg-transparent border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white font-bold uppercase tracking-wider px-8 py-4 text-lg rounded-full transition-all group"
                      data-testid={`contact-us-${service.id}`}
                    >
                      Contact Us
                      <ArrowRight className="ml-2 animate-bounce-x" size={20} />
                    </Button>
                  </Link>
                </div>
                )}
                </div>

                {/* Image with Carousel - Desktop only */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''} hidden md:block`}>
                  <div className="aspect-[4/3] bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
                    <ServiceImageCarousel images={service.images} name={service.title} darkArrows={service.darkArrows} />
                  </div>
                  
                  {/* View Products CTA - Desktop only */}
                  {service.productLink && (
                    <div className="mt-8 text-center">
                      <Link to={service.productLink}>
                        <Button 
                          className="bg-transparent border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-all"
                          data-testid={`view-products-${service.id}`}
                        >
                          View Products
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
            Need Something Custom?
          </h2>
          <p className="text-xl text-[#A3A3A3] mb-8">
            Don't see exactly what you're looking for? We specialise in custom solutions. 
            Tell us what you need and we'll make it happen.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full btn-glow"
              data-testid="services-cta-btn"
            >
              Discuss Your Project
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
